import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { sessionManager } from "../../services/sessionManager";
import { useAuth } from "../../contexts/AuthContext";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Drawer,
  List,
  Button,
  TextField,
  Divider,
  Tooltip,
  Fade,
  Chip,
  CircularProgress,
  ButtonGroup,
} from "@mui/material";
import {
  NavigateBefore,
  NavigateNext,
  ZoomIn,
  ZoomOut,
  Notes,
  Close,
  Delete,
  Edit,
  Fullscreen,
  FullscreenExit,
  Home,
  LastPage,
  ViewColumn,
  ViewStream,
  Download,
} from "@mui/icons-material";
import {
  booksAPI,
  annotationsAPI,
  readingProgressAPI,
  readingSessionAPI,
  accessRequestAPI,
} from "../../services/api";
import { useSnackbar } from "../../contexts/SnackbarContext";
import AccessRequestDialog from "../../components/books/AccessRequestDialog";

const API_URL =
  import.meta.env.VITE_API_URL;
import { GlobalWorkerOptions, version } from "pdfjs-dist/build/pdf";
GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;

const BookReader = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [book, setBook] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(null);
  const [scale, setScale] = useState(1.2);
  const [loading, setLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);
  const [accessResponse, setAccessResponse] = useState(null);
  const [openAccessDialog, setOpenAccessDialog] = useState(false);
  const isAdmin = user?.role === "admin";
  const [annotations, setAnnotations] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newAnnotation, setNewAnnotation] = useState("");
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [editingAnnotation, setEditingAnnotation] = useState(null);
  const [goToPageInput, setGoToPageInput] = useState("");
  const [error, setError] = useState(null);
  const [isPortrait, setIsPortrait] = useState(true);
  const [showTwoPages, setShowTwoPages] = useState(false);
  const [containerDimensions, setContainerDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [currentSession, setCurrentSession] = useState(null);
  const [sessionStatus, setSessionStatus] = useState("active");
  const inactivityTimeout = useRef(null);
  const sessionEnded = useRef(false);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const hasAddedOpeningEntry = useRef(false);
  const [hasDownloaded, setHasDownloaded] = useState(() => {
    // Check localStorage for download status
    const downloadStatus = localStorage.getItem(`book_${id}_downloaded`);
    return downloadStatus === "true";
  });
  const [requestPending, setRequestPending] = useState(() => {
    // Check localStorage for pending request status
    const pendingStatus = localStorage.getItem(`book_${id}_request_pending`);
    return pendingStatus === "true";
  });

  const documentRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimeout = useRef(null);
  const { showSnackbar } = useSnackbar();

  const shouldShowTwoPages = () => {
    if (!pageSize.width || !pageSize.height) return false;

    const containerWidth = isFullscreen
      ? containerDimensions.width * 0.95
      : window.innerWidth * 0.85;

    // Calculate total width needed for two pages with gap (20% of page width)
    const totalWidth = pageSize.width * scale * 2.2; // Two pages plus 20% gap

    // Check if we have enough space and screen is wide enough
    return (
      containerWidth >= totalWidth && window.innerWidth > 1200 && scale <= 2.0
    ); // Prevent two-page mode at very high zoom levels
  };

  const updatePageMode = () => {
    const canShowTwoPages = shouldShowTwoPages();
    if (showTwoPages !== canShowTwoPages) {
      setShowTwoPages(canShowTwoPages);
      if (!canShowTwoPages) {
        // When switching to single page mode, ensure we're on the correct page
        handlePageChange(pageNumber + (pageNumber % 2 === 1 ? 0 : 1));
      } else if (pageNumber % 2 === 0) {
        // When switching to two-page mode, ensure we start on an odd page
        handlePageChange(pageNumber - 1);
      }
    }
  };

  useEffect(() => {
    updatePageMode();
  }, [scale, containerDimensions, pageSize, isFullscreen]);

  const calculateInitialScale = (pageWidth, pageHeight) => {
    const container = containerRef.current;
    if (!container) return 1;

    const isPagePortrait = pageHeight > pageWidth;
    setIsPortrait(isPagePortrait);
    setPageSize({ width: pageWidth, height: pageHeight });

    const viewportHeight = isFullscreen
      ? (containerDimensions.height - 64) * 0.95 // Account for header height
      : window.innerHeight * 0.85;
    const viewportWidth = shouldShowTwoPages()
      ? (isFullscreen
          ? (containerDimensions.width - 32) * 0.95 // Account for padding
          : window.innerWidth * 0.85) / 2.2
      : isFullscreen
      ? (containerDimensions.width - 32) * 0.95 // Account for padding
      : window.innerWidth * 0.8;

    const scaleByHeight = viewportHeight / pageHeight;
    const scaleByWidth = viewportWidth / pageWidth;

    return Math.min(scaleByHeight, scaleByWidth, 2.5);
  };

  const handleZoom = (newScale) => {
    const adjustedScale = Math.max(0.5, Math.min(2.5, newScale));
    if (adjustedScale !== scale) {
      // Only update if scale actually changes
      setScale(adjustedScale);
      updatePageMode(); // Explicitly update page mode when zoom changes
    }
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const handlePageChange = async (newPage) => {
    if (!numPages) return;

    let adjustedPage = newPage;

    if (showTwoPages) {
      if (pageNumber + 1 >= numPages && newPage >= numPages - 1) {
        adjustedPage = 1;
        showSnackbar("Book completed! Starting over from page 1", "success");
      } else {
        adjustedPage = Math.max(1, newPage - (newPage % 2 === 0 ? 1 : 0));
      }
    } else {
      if (newPage > numPages) {
        adjustedPage = 1;
        showSnackbar("Reached the end - Starting over from page 1", "info");
      } else {
        adjustedPage = Math.max(1, Math.min(newPage, numPages));
      }
    }

    if (adjustedPage !== pageNumber) {
      setPageNumber(adjustedPage);
      try {
        const currentPage = showTwoPages ? adjustedPage + 1 : adjustedPage;
        const isLastPage = currentPage === numPages;

        await readingProgressAPI.update(id, {
          current_page: currentPage,
          ...(isLastPage && { completed: true }),
        });
      } catch (error) {
        console.error("Failed to update reading progress:", error);
        showSnackbar("Failed to update reading progress", "error");
      }
    }
  };

  const handleKeyDown = (e) => {
    if (
      e.target.tagName === "TEXTAREA" ||
      e.target.tagName === "INPUT" ||
      !numPages ||
      numPages === 0
    ) {
      return;
    }

    switch (e.key) {
      case "ArrowRight":
      case "PageDown":
        e.preventDefault();
        handlePageChange(
          showTwoPages ? Math.min(numPages, pageNumber + 2) : pageNumber + 1
        );
        break;
      case "ArrowLeft":
      case "PageUp":
        if (pageNumber > 1) {
          e.preventDefault();
          handlePageChange(
            showTwoPages ? Math.max(1, pageNumber - 2) : pageNumber - 1
          );
        }
        break;
      case "Home":
        if (pageNumber !== 1) {
          e.preventDefault();
          handlePageChange(1);
        }
        break;
      case "End":
        if (pageNumber !== numPages) {
          e.preventDefault();
          handlePageChange(numPages);
        }
        break;
      case "f":
        e.preventDefault();
        toggleFullscreen();
        break;
      case "-":
        e.preventDefault();
        if (scale > 0.5) {
          handleZoom(scale - 0.2);
        }
        break;
      case "+":
      case "=":
        e.preventDefault();
        if (scale < 2.5) {
          handleZoom(scale + 0.2);
        }
        break;
    }
  };
  const startReadingSession = async () => {
    if (sessionEnded.current) return;

    try {
      // Start or get existing session
      const session = await readingSessionAPI.startSession(id);

      // Register with session manager and update state
      if (session) {
        // Store session ID in a ref to persist through unmount
        window.currentSessionId = session.id;
        window.currentSessionStartTime = session.start_time;

        sessionManager.registerSession(id, session.id, session.start_time);
        setCurrentSession(session);
        setSessionStatus(session.status);

        console.log("Reading session started:", {
          sessionId: session.id,
          startTime: session.start_time,
        });
      }
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.error("Failed to start reading session:", error);
        showSnackbar("Failed to start reading session", "error");
      }
    }
  };

  const pauseReadingSession = async () => {
    if (currentSession && sessionStatus === "active") {
      try {
        await readingSessionAPI.pauseSession(id, currentSession.id);
        setSessionStatus("paused");
      } catch (error) {
        console.error("Failed to pause reading session:", error);
      }
    }
  };

  const resumeReadingSession = async () => {
    if (currentSession && sessionStatus === "paused") {
      try {
        await readingSessionAPI.resumeSession(id, currentSession.id);
        setSessionStatus("active");
      } catch (error) {
        console.error("Failed to resume reading session:", error);
      }
    }
  };

  const endReadingSession = async () => {
    console.log("Attempting to end reading session", {
      sessionEnded: sessionEnded.current,
      hasActiveSession: sessionManager.hasActiveSession(id),
      currentSession: currentSession,
      persistedSessionId: window.currentSessionId,
    });

    if (sessionEnded.current) {
      console.log("Session already marked as ended, skipping");
      return;
    }

    if (!sessionManager.hasActiveSession(id)) {
      console.log("No active session found in session manager");
      return;
    }

    const sessionId = window.currentSessionId || currentSession?.id;
    if (!sessionId) {
      console.log("No session ID available");
      return;
    }

    try {
      // First end the session in the backend
      const xhr = new XMLHttpRequest();
      const endpoint = `${API_URL}/books/${id}/sessions/${sessionId}/end`;
      console.log("Ending session at endpoint:", endpoint);

      xhr.open("POST", endpoint, false);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.setRequestHeader(
        "Authorization",
        `Bearer ${localStorage.getItem("token")}`
      );
      xhr.send();

      // Then update local state
      if (sessionManager.endSession(id)) {
        console.log("Session ended successfully");
        sessionEnded.current = true;
        setCurrentSession(null);
        setSessionStatus("completed");
      }
    } catch (error) {
      console.error("Error ending reading session:", error);
    }
  };

  const handleActivity = () => {
    setShowControls(true);
    clearTimeout(controlsTimeout.current);
    controlsTimeout.current = setTimeout(() => setShowControls(false), 3000);

    // Reset inactivity timer
    clearTimeout(inactivityTimeout.current);
    if (sessionStatus === "active") {
      inactivityTimeout.current = setTimeout(() => {
        pauseReadingSession();
      }, 5 * 60 * 1000); // Pause after 5 minutes of inactivity
    }

    // Resume session if it was paused
    if (sessionStatus === "paused") {
      resumeReadingSession();
    }
  };

  useEffect(() => {
    if (numPages !== null && !loading) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [numPages, loading, showTwoPages, pageNumber]);

  useEffect(() => {
    // Create cleanup function that ensures synchronous execution
    const cleanup = () => {
      console.log("Running cleanup function", {
        sessionEnded: sessionEnded.current,
        currentPage: pageNumber,
        totalPages: numPages,
        currentSession: currentSession,
      });

      if (!sessionEnded.current) {
        try {
          endReadingSession();
        } catch (error) {
          console.error("Cleanup error:", error);
        }
      } else {
        console.log("Session already ended, skipping cleanup");
      }
    };

    // Check if we need to reset the downloaded flag based on new approvals
    const checkAccessStatus = async () => {
      if (
        !isAdmin &&
        localStorage.getItem(`book_${id}_downloaded`) === "true"
      ) {
        try {
          const response = await accessRequestAPI.getForBook(id);
          const hasApprovedRequest = response.data?.some(
            (req) => req.user_id === user?.id && req.status === "approved"
          );

          if (hasApprovedRequest) {
            // If there's a new approved request, reset the downloaded flag
            setHasDownloaded(false);
            localStorage.removeItem(`book_${id}_downloaded`);
          }
        } catch (error) {
          console.error("Error checking access status:", error);
        }
      }
    };

    // Initial setup
    fetchData();
    checkAccessStatus(); // Check access status on mount
    document.addEventListener("mousemove", handleActivity);
    document.addEventListener("keydown", handleActivity);
    document.addEventListener("fullscreenchange", () =>
      setIsFullscreen(Boolean(document.fullscreenElement))
    );

    // Start reading session only on initial mount
    if (!sessionEnded.current) {
      startReadingSession();
    }

    // Attach cleanup handlers
    const handleBeforeUnload = (e) => {
      cleanup();
      e.preventDefault();
      e.returnValue = "";
    };

    const handlePopState = () => {
      cleanup();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    // Cleanup on unmount
    return () => {
      document.removeEventListener("mousemove", handleActivity);
      document.removeEventListener("keydown", handleActivity);
      document.removeEventListener("fullscreenchange", () => {});
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      clearTimeout(controlsTimeout.current);
      clearTimeout(inactivityTimeout.current);

      console.log("Component unmounting - running final cleanup");
      cleanup();

      // Clean up window variables
      delete window.currentSessionId;
      delete window.currentSessionStartTime;
      delete window.isCleanupHandled;
    };
  }, [id]); // Keep only id dependency

  const fetchData = async () => {
    try {
      const [
        bookResponse,
        annotationsResponse,
        progressResponse,
        accessResponse,
      ] = await Promise.all([
        booksAPI.getById(id),
        annotationsAPI.getForBook(id),
        readingProgressAPI.get(id),
        !isAdmin ? accessRequestAPI.getForBook(id) : Promise.resolve([]),
      ]);

      const bookData = {
        ...bookResponse.data.book,
        completed: progressResponse.data?.progress?.completed || false,
        pdf_url: bookResponse.data.book.file_path.startsWith("http")
          ? bookResponse.data.book.file_path
          : `${API_URL}/uploads/books/${bookResponse.data.book.file_path
              .split("/")
              .pop()}`,
      };
      setBook(bookData);

      setAnnotations(
        Array.isArray(annotationsResponse.data?.annotations)
          ? annotationsResponse.data.annotations
          : []
      );

      // Set access status and response for non-admin users
      if (!isAdmin) {
        setAccessResponse(accessResponse.data || []);
        const latestRequest = accessResponse.data?.find(
          (req) => req.user_id === user.id
        );
        const newStatus = latestRequest ? latestRequest.status : null;
        setAccessStatus(newStatus);

        // If there's an approved request, reset the requestPending state
        if (newStatus === "approved") {
          setRequestPending(false);
          localStorage.removeItem(`book_${id}_request_pending`);

          // Check if we have a downloaded flag but a new approved request
          // This means the user has downloaded before and now has a new approved request
          const hasDownloadedBefore =
            localStorage.getItem(`book_${id}_downloaded`) === "true";
          if (hasDownloadedBefore) {
            // Reset the downloaded flag so they can download again without seeing the request dialog
            setHasDownloaded(false);
            localStorage.removeItem(`book_${id}_downloaded`);
          }
        }
      }

      const progress = progressResponse.data?.progress;
      if (progress) {
        const currentPage = progress.current_page;
        if (currentPage > 0) {
          // Add history entry when opening the book (if not already added)
          if (!hasAddedOpeningEntry.current) {
            hasAddedOpeningEntry.current = true;
            const actionType = currentPage === 1 ? "started" : "resumed";
            await readingProgressAPI.addHistoryEntry(id, actionType);
          }

          setPageNumber(currentPage);
          showSnackbar(`Resuming from page ${currentPage}`, "info");
          return;
        }
      }

      // Add "started" history entry for first-time reading (if not already added)
      if (!hasAddedOpeningEntry.current) {
        hasAddedOpeningEntry.current = true;
        await readingProgressAPI.addHistoryEntry(id, "started");
      }
      setPageNumber(1);
    } catch (error) {
      console.error("Error:", error);
      setError("Failed to load book data");
      showSnackbar("Failed to load book data", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        const element = containerRef.current;
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      }
    } catch (error) {
      console.error("Fullscreen error:", error);
      showSnackbar("Fullscreen mode not supported", "error");
    }
  };

  const toggleTwoPages = () => {
    const canShowTwoPages = shouldShowTwoPages();
    if (canShowTwoPages) {
      setShowTwoPages((prev) => !prev);
      if (!showTwoPages && pageNumber % 2 === 0) {
        handlePageChange(pageNumber - 1);
      }
    }
  };

  const handleAddAnnotation = async () => {
    if (!newAnnotation.trim()) return;

    try {
      if (editingAnnotation && editingAnnotation.id) {
        // Validate that we have a valid ID before proceeding with update
        const response = await annotationsAPI.update(id, editingAnnotation.id, {
          content: newAnnotation,
          page_number: editingAnnotation.page_number,
          annotation_type: "note",
        });

        setAnnotations((prev) =>
          prev.map((a) =>
            a.id === editingAnnotation.id
              ? {
                  ...editingAnnotation,
                  ...response.data,
                  content: newAnnotation,
                  page_number: editingAnnotation.page_number,
                }
              : a
          )
        );
        setEditingAnnotation(null);
        showSnackbar("Note updated successfully", "success");
      } else if (editingAnnotation) {
        // Handle the case where editingAnnotation exists but ID is invalid
        // Create a new annotation instead of updating
        const newNote = {
          page_number: editingAnnotation.page_number || pageNumber,
          content: newAnnotation,
          annotation_type: "note",
        };
        
        const response = await annotationsAPI.create(id, newNote);
        
        // Remove the old annotation from state if it exists
        setAnnotations(prev => prev.filter(a => a !== editingAnnotation));
        
        // Add the new one
        setAnnotations(prev => [
          ...prev,
          {
            ...newNote,
            ...response.data,
            id: response.data.id,
            created_at: response.data.created_at,
          }
        ]);
        
        setEditingAnnotation(null);
        showSnackbar("Note created successfully", "success");
      } else {
        // Regular new annotation
        const newNote = {
          page_number: pageNumber,
          content: newAnnotation,
          annotation_type: "note",
          created_at: new Date().toISOString(), // Ensure we always have a created_at date
        };

        const response = await annotationsAPI.create(id, newNote);

        setAnnotations((prev) => [
          ...prev,
          {
            ...newNote,
            ...response.data,
            id: response.data.id,
            created_at: response.data.created_at,
          },
        ]);
        showSnackbar("Note added successfully", "success");
      }
      setNewAnnotation("");
    } catch (error) {
      showSnackbar(
        `Failed to ${editingAnnotation ? "update" : "add"} note`,
        "error"
      );
    }
  };

  const handleEditAnnotation = (annotation) => {
    setEditingAnnotation(annotation);
    setNewAnnotation(annotation.content);
  };

  const handleGoToPage = (e) => {
    e.preventDefault();
    const page = parseInt(goToPageInput);
    if (page && page > 0 && page <= numPages) {
      handlePageChange(page);
      setGoToPageInput("");
    } else {
      showSnackbar(`Please enter a valid page number (1-${numPages})`, "error");
    }
  };

  const handleDeleteAnnotation = async (annotationId) => {
    try {
      await annotationsAPI.delete(id, annotationId);
      setAnnotations((prev) => prev.filter((a) => a.id !== annotationId));
      showSnackbar("Note deleted", "success");
    } catch (error) {
      showSnackbar("Failed to delete note", "error");
    }
  };

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-screen">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex justify-center items-center h-screen">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      className={`min-h-screen flex flex-col ${
        isFullscreen ? "h-screen w-screen" : ""
      }`}
      sx={{
        width: "100%",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "#121212" : "#e5e5e5",
        "& .react-pdf__Page": {
          height: "auto !important",
          maxWidth: "100%",
        },
        "& .react-pdf__Document": {
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          width: "100%",
          padding: "1rem",
          overflow: "auto",
        },
      }}
      tabIndex="0"
    >
      <Fade in={showControls || !isFullscreen}>
        <Paper
          elevation={0}
          className="p-4 flex items-center justify-between z-10 backdrop-blur-lg"
          sx={(theme) => ({
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(30, 30, 30, 0.9)"
                : "rgba(255, 255, 255, 0.9)",
          })}
        >
          <Box className="flex items-center gap-4">
            <Box className="flex items-center gap-2">
              <Typography variant="h5" className="font-semibold">
                {book?.title}
              </Typography>
              <Chip
                label={`${pageNumber}${
                  showTwoPages && pageNumber < numPages
                    ? `-${pageNumber + 1}`
                    : ""
                } / ${numPages}`}
                variant="outlined"
                size="small"
              />
              {currentSession && (
                <Tooltip
                  title={
                    sessionStatus === "paused"
                      ? "Reading session paused due to inactivity"
                      : "Active reading session"
                  }
                >
                  <Chip
                    label={sessionStatus === "paused" ? "Paused" : "Reading"}
                    color={sessionStatus === "paused" ? "warning" : "success"}
                    size="small"
                    variant="outlined"
                    sx={{
                      animation:
                        sessionStatus === "paused"
                          ? "pulse 2s infinite"
                          : "none",
                      "@keyframes pulse": {
                        "0%": { opacity: 1 },
                        "50%": { opacity: 0.6 },
                        "100%": { opacity: 1 },
                      },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>

          <Box className="flex items-center gap-3">
            <ButtonGroup size="small" variant="outlined">
              <Tooltip title="Toggle Two Pages">
                <span>
                  <IconButton
                    onClick={toggleTwoPages}
                    disabled={!shouldShowTwoPages()}
                  >
                    {showTwoPages ? <ViewStream /> : <ViewColumn />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Zoom Out (-)">
                <span>
                  <IconButton
                    onClick={() => handleZoom(scale - 0.2)}
                    disabled={scale <= 0.5}
                  >
                    <ZoomOut fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Zoom In (+)">
                <span>
                  <IconButton
                    onClick={() => handleZoom(scale + 0.2)}
                    disabled={scale >= 2.5}
                  >
                    <ZoomIn fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Toggle Fullscreen (F)">
                <span>
                  <IconButton onClick={toggleFullscreen}>
                    {isFullscreen ? (
                      <FullscreenExit fontSize="small" />
                    ) : (
                      <Fullscreen fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Toggle Notes">
                <span>
                  <IconButton
                    onClick={() => setDrawerOpen(true)}
                    className={
                      Array.isArray(annotations) &&
                      annotations.some((a) => a.page_number === pageNumber)
                        ? "text-blue-600"
                        : ""
                    }
                  >
                    <Notes fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              {/* Download button with access control */}
              {isAdmin || accessStatus === "approved" ? (
                <Tooltip
                  title={
                    hasDownloaded && !isAdmin
                      ? requestPending
                        ? "Access Request Pending"
                        : "Request Access Again"
                      : "Download Book"
                  }
                >
                  <span>
                    <IconButton
                      onClick={async () => {
                        // If a request is pending, don't do anything
                        if (requestPending) return;
                        try {
                          const sectionRequest = accessResponse?.find(
                            (req) =>
                              req.status === "approved" &&
                              req.request_type === "section"
                          );

                          const fullRequest = accessResponse?.find(
                            (req) =>
                              req.status === "approved" &&
                              req.request_type === "full"
                          );

                          // First, refresh the access status to make sure we have the latest data
                          if (!isAdmin && hasDownloaded) {
                            try {
                              // Get the latest access requests
                              const refreshResponse =
                                await accessRequestAPI.getForBook(book.id);
                              setAccessResponse(refreshResponse.data || []);

                              // Check if there's an approved request
                              const hasApprovedRequest =
                                refreshResponse.data?.some(
                                  (req) =>
                                    req.user_id === user?.id &&
                                    req.status === "approved"
                                );

                              if (hasApprovedRequest) {
                                // If there's an approved request, reset the downloaded flag
                                setHasDownloaded(false);
                                localStorage.removeItem(
                                  `book_${book.id}_downloaded`
                                );
                                // Continue with download
                              } else {
                                // If no approved request, open the dialog
                                setOpenAccessDialog(true);
                                return;
                              }
                            } catch (error) {
                              console.error(
                                "Error refreshing access status:",
                                error
                              );
                              // If there's an error, just continue with the existing logic
                              if (hasDownloaded) {
                                setOpenAccessDialog(true);
                                return;
                              }
                            }
                          }

                          if (sectionRequest) {
                            await booksAPI.download(
                              book.id,
                              sectionRequest.section_start,
                              sectionRequest.section_end
                            );
                            showSnackbar("Download successful!", "success");
                            // Mark as downloaded and save to localStorage
                            setHasDownloaded(true);
                            localStorage.setItem(
                              `book_${book.id}_downloaded`,
                              "true"
                            );
                          } else if (fullRequest || isAdmin) {
                            await booksAPI.download(book.id);
                            showSnackbar("Download successful!", "success");
                            // Mark as downloaded for non-admin users and save to localStorage
                            if (!isAdmin) {
                              setHasDownloaded(true);
                              localStorage.setItem(
                                `book_${book.id}_downloaded`,
                                "true"
                              );
                            }
                          } else {
                            showSnackbar(
                              "No valid download permission found",
                              "error"
                            );
                          }
                        } catch (error) {
                          console.error("Download error:", error);
                          
                          // Enhanced error handling for 403 Forbidden errors
                          if (error.response?.status === 403) {
                            // If trying to download a section, provide specific guidance
                            if (sectionRequest) {
                              setError({
                                title: "Access Denied",
                                message: `You don't have permission to download section ${sectionRequest.section_start}-${sectionRequest.section_end}. This may be because your approved section range is different or your access has expired.`,
                                action: "requestAccess"
                              });
                              
                              // Open the access dialog to let them request correct access
                              setOpenAccessDialog(true);
                            } else {
                              setError({
                                title: "Access Denied",
                                message: "You don't have permission to download this book. Please request access first.",
                                action: "requestAccess"
                              });
                              
                              // Open the access dialog
                              setOpenAccessDialog(true);
                            }
                            
                            // Show professional error message with guidance
                            showSnackbar(
                              "Access denied. Please request or update your access permissions.",
                              "error"
                            );
                          } 
                          // Add enhanced handling for 500 Internal Server Errors
                          else if (error.response?.status === 500) {
                            console.log('Server processing error details:', error.response?.data);
                            
                            // If trying to download a section, provide helpful guidance
                            if (sectionRequest) {
                              setError({
                                title: "Download Failed",
                                message: `The server encountered an error processing section ${sectionRequest.section_start}-${sectionRequest.section_end}. This might be because the section is invalid or the PDF file has issues.`,
                                action: "tryDifferent"
                              });
                              
                              // Show a more professional error message with guidance
                              showSnackbar(
                                "Book section could not be processed. Please try a different section or contact support.",
                                "error"
                              );
                            } else {
                              showSnackbar(
                                "The server encountered an error processing this book. Please try again later or contact support.",
                                "error"
                              );
                            }
                          } else {
                            // For other errors, show the default message
                            showSnackbar(
                              error.response?.data?.message ||
                                "Error downloading book",
                              "error"
                            );
                          }
                        }
                      }}
                      disabled={loading || (hasDownloaded && requestPending)}
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : accessStatus === "pending" ? (
                <Tooltip title="Download Request Pending">
                  <span>
                    <IconButton disabled>
                      <Download fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Request Download Access">
                  <span>
                    <IconButton
                      onClick={() => setOpenAccessDialog(true)}
                      disabled={requestPending}
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </ButtonGroup>
          </Box>
        </Paper>
      </Fade>

      <Box
        className="flex-1 overflow-auto flex justify-center"
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === "dark" ? "#121212" : "#e5e5e5",
          height: isFullscreen ? "calc(100vh - 64px)" : "auto",
          overflowY: "auto",
          overflowX: "hidden",
        })}
      >
        <Document
          file={book?.pdf_url}
          onLoadSuccess={({ numPages: num }) => {
            setNumPages(num);
            if (pageNumber === num) {
              setPageNumber(1);
              showSnackbar("Starting from page 1", "info");
            } else if (pageNumber === null) {
              setPageNumber(1);
            }
          }}
          loading={<CircularProgress />}
          error={<Typography color="error">Failed to load PDF</Typography>}
          ref={documentRef}
          className="w-full flex justify-center px-4"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "flex-start",
              width: "100%",
              minHeight: "100%",
              padding: "1rem 0",
              gap: "2rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "2rem",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Page
                key={`page-${pageNumber}`}
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                className="shadow-2xl"
                onLoadSuccess={(page) => {
                  if (scale === 1.2) {
                    // Only set initial scale if we haven't zoomed yet
                    const newScale = calculateInitialScale(
                      page.originalWidth,
                      page.originalHeight
                    );
                    setScale(newScale);
                  }
                  const isLandscape = page.originalWidth > page.originalHeight;
                  setIsPortrait(!isLandscape);
                  setPageSize({
                    width: page.originalWidth,
                    height: page.originalHeight,
                  });
                }}
                loading={
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "50vh",
                    }}
                  >
                    <CircularProgress />
                  </Box>
                }
              />
              {showTwoPages && pageNumber < numPages && (
                <Page
                  key={`page-${pageNumber + 1}`}
                  pageNumber={pageNumber + 1}
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="shadow-2xl"
                  loading={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "50vh",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  }
                />
              )}
            </Box>
          </Box>
        </Document>

        <Fade in={showControls}>
          <Box
            className="fixed bottom-8 left-1/2 transform -translate-x-1/2 p-2 rounded-full shadow-lg backdrop-blur-sm"
            sx={(theme) => ({
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(30, 30, 30, 0.9)"
                  : "rgba(255, 255, 255, 0.9)",
              "& .MuiButtonGroup-root": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.23)"
                    : "rgba(0, 0, 0, 0.23)",
              },
            })}
          >
            <ButtonGroup variant="outlined" size="small">
              <Tooltip title="First Page (Home)">
                <span>
                  <IconButton
                    onClick={() => handlePageChange(1)}
                    disabled={pageNumber <= 1}
                  >
                    <Home />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Previous Page (←)">
                <span>
                  <IconButton
                    onClick={() =>
                      handlePageChange(
                        showTwoPages
                          ? Math.max(1, pageNumber - 2)
                          : Math.max(1, pageNumber - 1)
                      )
                    }
                    disabled={pageNumber <= 1}
                  >
                    <NavigateBefore />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next Page (→) - Returns to first page when at the end">
                <span>
                  <IconButton
                    onClick={() =>
                      handlePageChange(
                        showTwoPages
                          ? Math.min(numPages, pageNumber + 2)
                          : Math.min(numPages, pageNumber + 1)
                      )
                    }
                  >
                    <NavigateNext />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Last Page (End)">
                <span>
                  <IconButton
                    onClick={() => handlePageChange(numPages)}
                    disabled={pageNumber >= numPages}
                  >
                    <LastPage />
                  </IconButton>
                </span>
              </Tooltip>
            </ButtonGroup>
          </Box>
        </Fade>
      </Box>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: "500px" },
            padding: 4,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#1a1a1a" : "#f8f9fa",
          },
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          className="flex justify-between items-center mb-6"
        >
          <Typography variant="h6" className="font-semibold">
            All Notes
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)}>
            <Close />
          </IconButton>
        </Box>

        <Divider />

        <Box className="mb-8">
          <Paper className="p-6 mb-8" variant="outlined">
            <form onSubmit={handleGoToPage} className="mb-6 flex gap-3">
              <Box display={"flex"}>
                <TextField
                  size="small"
                  type="number"
                  label="Go to page"
                  variant="outlined"
                  fullWidth
                  value={goToPageInput}
                  onChange={(e) => setGoToPageInput(e.target.value)}
                  inputProps={{ min: 1, max: numPages }}
                  helperText={`Total pages: ${numPages}`}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!goToPageInput}
                  sx={{ height: 40 }}
                >
                  Go
                </Button>
              </Box>
            </form>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={editingAnnotation ? "Edit note" : "Add note"}
              placeholder="Write your thoughts here..."
              value={newAnnotation}
              onChange={(e) => setNewAnnotation(e.target.value)}
              className="mb-4"
              variant="outlined"
            />
            <Box className="flex gap-2">
              <Button
                fullWidth
                variant="contained"
                onClick={handleAddAnnotation}
                size="large"
                disabled={!newAnnotation.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {editingAnnotation ? "Update Note" : "Add Note"}
              </Button>
              {editingAnnotation && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditingAnnotation(null);
                    setNewAnnotation("");
                  }}
                  size="large"
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Paper>
        </Box>

        <List className="space-y-4">
          {annotations
            .sort((a, b) => {
              const pageComparison = b.page_number - a.page_number;
              if (pageComparison !== 0) return pageComparison;
              return (b.created_at || "").localeCompare(a.created_at || "");
            })
            .map((annotation) => (
              <Paper
                key={`${annotation.id}-${annotation.page_number}`}
                className="p-5"
                elevation={0}
                sx={(theme) => ({
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(30, 30, 30, 0.6)"
                      : "rgba(245, 245, 245, 0.8)",
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(40, 40, 40, 0.8)"
                        : "rgba(255, 255, 255, 0.9)",
                    transform: "translateX(4px)",
                  },
                })}
              >
                <Box m={"10px"} className="flex flex-col gap-3">
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    className="flex justify-between items-start gap-4"
                  >
                    <Typography
                      className="flex-1"
                      sx={{
                        fontSize: "1rem",
                        lineHeight: 1.6,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {annotation.content}
                    </Typography>
                    <Box className="flex gap-1">
                      <Tooltip title="Edit note">
                        <IconButton
                          size="small"
                          onClick={() => handleEditAnnotation(annotation)}
                          className="text-gray-500 hover:text-blue-500"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete note">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteAnnotation(annotation.id)}
                          className="text-gray-500 hover:text-red-500"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box
                    className="flex items-center gap-3 mt-1"
                    display={"flex"}
                    justifyContent={"space-between"}
                  >
                    <Chip
                      label={`Page ${annotation.page_number}`}
                      size="small"
                      variant="outlined"
                      color={
                        annotation.page_number === pageNumber
                          ? "primary"
                          : "default"
                      }
                      onClick={() => handlePageChange(annotation.page_number)}
                      sx={{ cursor: "pointer" }}
                    />
                    <Typography
                      variant="caption"
                      className="text-gray-500"
                      sx={{ fontSize: "0.70rem" }}
                    >
                      Added{" "}
                      {new Date(annotation.created_at && !isNaN(new Date(annotation.created_at)) 
                        ? annotation.created_at
                        : new Date() // Use current date when created_at is missing or invalid
                      ).toLocaleDateString(
                        undefined,
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
        </List>
      </Drawer>

      {/* Access Request Dialog */}
      <AccessRequestDialog
        open={openAccessDialog}
        onClose={() => setOpenAccessDialog(false)}
        bookId={id}
        totalPages={numPages}
        onRequestSubmitted={(request) => {
          // Mark request as pending
          setRequestPending(true);
          // Store in localStorage to persist across refreshes
          localStorage.setItem(`book_${id}_request_pending`, "true");
          // Update access status
          setAccessStatus("pending");
          // Update access response
          setAccessResponse((prev) => [...(prev || []), request]);
          showSnackbar(
            "Download access request submitted successfully",
            "success"
          );
        }}
      />
    </Box>
  );
};

export default BookReader;
